import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TabControlComponent } from '../../../../shared/controls/tab-control/tab-control.component';
import { TabPageComponent } from '../../../../shared/controls/tab-control/tab-page.component';
import { AutoCompleteDropdownComponent } from '../../../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';

import { ToastService } from '../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../shared/services/error-service';

import { EndorsementService, AllEndorsementTypeItem, UpdateEndorsementInput, AllEndorsementTypeValueItem } from '../../../../../service/proxy';

@Component({
    selector: 'create-or-update-endorsement',
    templateUrl: './create-or-update-endorsement.component.html',
    styleUrls: ['./create-or-update-endorsement.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        TabControlComponent,
        TabPageComponent,
        AutoCompleteDropdownComponent
    ],

    providers: [
        EndorsementService
    ]
})
export class CreateOrUpdateEndorsementComponent implements OnInit {
    @ViewChild('form')
    form: NgForm | undefined;

    public clientId: number = 0;
    public policyId: number = 0;
    public endorsementId: number = 0;

    public selectedInputType: string = '';
    public selectedEndorsementType: string = '';
    public selectedEndorsementTypeId: number = 0;
    public selectedEndorsementTypeValue: string = '';
    public selectedEndorsementTypeValueId: number | undefined = undefined;

    public expiryDateError = false;
    public expiryTypingTimer: any;

    public endorsementTypes: AllEndorsementTypeItem[] = [];
    public endorsementTypeValues: AllEndorsementTypeValueItem[] = [];

    public isSaving: boolean = false;
    public formSubmitted: boolean = false;
    public inViewMode: boolean = false;
    public readOnly: boolean = false;

    public endorsement: any = {
        clientAreaId: 0,
        endorsementType: '',
        values: '',
        effectiveDate: '',
        expiryDate: ''
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastService: ToastService,
        private errorService: ErrorService,
        private endorsementService: EndorsementService,
        private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.route.paramMap.subscribe(params => {
            this.clientId = Number(params.get('clientId'));
            this.policyId = Number(params.get('policyId'));
            this.endorsementId = Number(params.get('endorsementId'));

            this.endorsement.clientAreaId = this.policyId;

            this.getEndorsementTypes().add(() => {
                this.getEndorsementTypeValues().add(() => {
                    if (this.endorsementId > 0) {
                        this.getEndorsementById();

                        const today = new Date();
                        if(this.endorsement.expiryDate < today){
                            this.readOnly = true;
                        }
                    }
                    else {
                        this.setDefaultEndorsementDates();
                    }
                });
            });
        });
    }

    getEndorsementTypes() {
        return this.endorsementService.getAllEndorsementTypes()
            .subscribe((data) => {
                this.endorsementTypes = data.results || [];
            });
    }

    getEndorsementTypeValues() {
        return this.endorsementService.getAllEndorsementTypeValues()
            .subscribe((data) => {
                this.endorsementTypeValues = data.results || [];
            });
    }

    isExpired(): void {
        const expiry = new Date(this.endorsement.expiryDate);
        const today = new Date();

        today.setHours(0, 0, 0, 0);
        expiry.setHours(0, 0, 0, 0);

        if (expiry.getFullYear() >= 1000 && today >= expiry) {
            this.inViewMode = true;
        }
        else {
            this.inViewMode = false;
        }
    }

    getEndorsementById() {
        this.endorsementService.getEndorsementById(this.endorsementId)
            .subscribe({
                next: (result: any) => {
                    this.endorsement = result;

                    this.isExpired();
                    const today = new Date();
                    this.readOnly = this.endorsement.expiryDate < today;

                    const selectedType = this.endorsementTypes.find(e => e.description === this.endorsement.endorsementType);

                    if (selectedType) {
                        this.selectedEndorsementTypeId = selectedType.id;
                        this.selectedEndorsementType = selectedType.description;
                        this.getEndorsementInputType(selectedType.id);
                        this.endorsement.values = result.values;

                        this.endorsementService.getFilteredEndorsementTypeValues(selectedType.id).subscribe({
                            next: (data) => {
                                this.endorsementTypeValues = data.results;
                                const selectedTypeValue = this.endorsementTypeValues.find(e => e.description === result.values);

                                if (selectedTypeValue) {
                                    this.selectedEndorsementTypeValueId = selectedTypeValue.id;
                                    this.selectedEndorsementTypeValue = selectedTypeValue.description;
                                }
                                else {
                                    this.selectedEndorsementTypeValueId = undefined;
                                    this.selectedEndorsementTypeValue = '';
                                }
                            },
                            error: () => {
                                this.endorsementTypeValues = [];
                                this.selectedEndorsementTypeValueId = undefined;
                                this.selectedEndorsementTypeValue = '';
                            }
                        });
                    }
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    getEndorsementInputType(endorsementTypeId: number): void {
        this.endorsementService.getFilteredEndorsementTypeValues(endorsementTypeId)
            .subscribe({
                next: (data) => {
                    this.selectedInputType = data.results.length > 1 ? 'dropdown' : 'text';

                    if (this.selectedInputType === 'text') {
                        this.endorsementTypeValues = [];
                        const singleResult = data.results[0];

                    if (singleResult && singleResult.description) {
                        switch (singleResult.description) {
                            case 'R':
                                this.selectedInputType = 'rand';
                                break;
                            case '%':
                                this.selectedInputType = 'percent';
                                break;
                            case 'Number':
                                this.selectedInputType = 'number';
                                break;
                            case 'Multiline-Text':
                                this.selectedInputType = 'multiline-text';
                                break;
                            default:
                                break;
                            }
                        }
                    }
                    else {
                        this.endorsementTypeValues = data.results;
                    }
                },
                error: () => {
                    this.endorsementTypeValues = [];
                }
            }
        );
    }

    onEndorsementTypeChange(event: any) {
        const selectedId = +event.target.value;
        const selected = this.endorsementTypes.find(e => e.id == selectedId);

        if (selected) {
            this.selectedEndorsementType = selected.description;
            this.endorsement.values = '';
            this.getEndorsementInputType(selected.id);
        }
    }

    onEndorsementTypeSelectionChange(selectedId: number | null): void {
        this.selectedEndorsementTypeId = selectedId ?? 0;
        const selected = this.endorsementTypes.find(e => e.id === selectedId);

        if (selected) {
            this.selectedEndorsementType = selected.description;
            this.endorsement.values = '';
            this.getEndorsementInputType(selected.id);
        }
    }

    onEndorsementTypeValueSelectionChange(selectedId: number | null): void {
        this.selectedEndorsementTypeValueId = selectedId ?? undefined;
        const selected = this.endorsementTypeValues.find(v => v.id === selectedId);
        this.endorsement.values = selected?.description ?? '';
    }

    onSave(form: any): void {
        this.formSubmitted = true;

        if (!form.valid || !this.selectedEndorsementTypeId) {
            return;
        }

        this.isSaving = true;

        if (this.endorsementId === 0) {
            this.createEndorsement();
        }
        else {
            this.updateEndorsement();
        }
    }

    createEndorsement(): void {
        this.endorsement.endorsementType = this.selectedEndorsementType;

        this.endorsementService.createEndorsement(this.endorsement)
            .subscribe({
                next: () => {
                    this.router.navigate([`/client-area/${this.clientId}/${this.policyId}`]);
                },
                error: (error: any) => {
                    this.isSaving = false;

                    if (error?.message) {
                        this.toastService.show(error.message, 'error');
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                },
                complete: () => {
                    this.isSaving = false;
                    this.toastService.show('Endorsement created successfully.', 'success');
                }
            }
        );
    }

    updateEndorsement(): void {
        const input = this.endorsement as UpdateEndorsementInput;

        this.endorsementService.updateEndorsement(this.endorsementId, input)
            .subscribe({
                next: () => {
                    this.router.navigate([`/client-area/${this.clientId}/${this.policyId}`]);
                },
                error: (error: any) => {
                    this.isSaving = false;

                    if (error?.message) {
                        this.toastService.show(error.message, 'error');
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                },
                complete: () => {
                    this.isSaving = false;
                    this.toastService.show('Endorsement updated successfully.', 'success');
                }
            }
        );
    }

    onExpiryDateInput(): void {
        clearTimeout(this.expiryTypingTimer);
            this.expiryTypingTimer = setTimeout(() => {
                this.validateExpiryDate();
            },
        );
    }

    validateExpiryDate(): void {
        const effectiveDateStr = this.endorsement.effectiveDate;
        const expiryDateStr = this.endorsement.expiryDate;

        const fullDateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (fullDateRegex.test(effectiveDateStr) && fullDateRegex.test(expiryDateStr)) {
            const effectiveDate = new Date(effectiveDateStr);
            const expiryDate = new Date(expiryDateStr);

            const expiryYear = expiryDate.getFullYear();

            // Ignore premature values like year 0002
            if (expiryYear < 1000) {
                this.expiryDateError = false;
                return;
            }

            if (expiryDate < effectiveDate) {
                this.expiryDateError = true;
                this.endorsement.expiryDate = '9999-12-31';
            }
            else {
                this.expiryDateError = false;
            }
        }
    }

    formatDate(date: Date): string {
        return date.toISOString().split('T')[0];
    }

    onCancel() {
        if (this.endorsementId > 0) {
            this.getEndorsementById();
        }
        else {
            this.selectedEndorsementTypeId = 0;
            this.selectedEndorsementTypeValueId = undefined;
            this.endorsement.value = undefined;

            this.selectedInputType = '';
            this.selectedEndorsementType = '';
            this.selectedEndorsementTypeId = 0;
            this.selectedEndorsementTypeValue = '';
            this.selectedEndorsementTypeValueId = undefined;

            this.setDefaultEndorsementDates();

            if (this.form) {
                this.form.resetForm(this.endorsement);
            }
            this.cdr.detectChanges();
        }
    }

    setDefaultEndorsementDates(): void {
        const today = new Date();
        const expiryDate = new Date('9999-12-31');
        this.endorsement.effectiveDate = this.formatDate(today);
        this.endorsement.expiryDate = this.formatDate(expiryDate);
    }

    onBack(): void {
        this.router.navigate([`/client-area/${this.clientId}/${this.policyId}`]);
    }

    displayEndorsementType(option: any): string {
        return option?.description || '';
    }

    displayEndorsementTypeValue(option: any): string {
        return option?.description || '';
    }
}